/**
 * The overworld map (docs/rpg.md §22).
 *
 * An archipelago: one island per topic, six offshore isles, and the sea between
 * them. Drawn as text, because that is the only format an author can see a
 * coastline in. One character per tile:
 *
 * ```
 *   .  grass      #  road        ~  water      *  snow
 *   f  forest     =  bridge      ^  mountain   -  dune
 *   ,  sand       "  crop
 * ```
 *
 * **Each topic has its own island**, and that is the whole shape of the world.
 * A single continent said the kingdoms were parts of one landmass; an
 * archipelago says they are ten places, each with its own ground, joined by
 * causeways you can see. Trigonometry is peaks, Statistics is a fen, the
 * Equation Forest is a forest, Linear Algebra is laid out in rows, and Calculus
 * is high and has snow on it. The chain runs south-west to north-east, roughly
 * the order the curriculum is met, and Starter Village is the island you wake
 * up on.
 *
 * **Every island is joined to the others, and that is not decoration.**
 * `validateOverworld` walks the map from the starting tile on every
 * `npm run content:validate` and fails the build if a gate or an isle landing
 * cannot be reached, because water between a student and a kingdom is
 * indistinguishable, from inside the game, from being told they are not good
 * enough for it. Fourteen causeways make the archipelago one connected place;
 * the sea is scenery.
 *
 * **The two long roads are the exception, and their lengths are the feature.**
 * Twenty-five tiles north from the Calculus island to North Reach, thirty south
 * from Starter Village to the Sunken Reach — the longest walks on the map, at
 * opposite ends of the world, so its middle reads as a middle. Both are pinned
 * by tests.
 *
 * **Nothing is drawn that a student cannot reach.** A crag can close a hollow
 * behind it, and ground you can see and never walk to reads as the map refusing
 * you — so the generator seals such pockets into the rock around them rather
 * than leaving them to be found. `content:validate` reports zero stranded
 * tiles, and that is the number to keep.
 *
 * **Terrain is scenery and never a lock**, at every scale and for every one of
 * these islands. The seals on kingdoms and the achievements on isles are the
 * only things that hold a story back, they are stated as mathematics, and
 * neither ever holds back a lesson.
 */

import type { Overworld } from '@yuan-math/world';

/**
 * Where a new adventurer wakes up: the meadow a few steps south of Starter
 * Village's gate, on its own island, close enough to see it and outside it —
 * so the first thing anybody does on this map is walk somewhere.
 */
const START = { x: 14, y: 103 };

export const OVERWORLD: Overworld = {
  width: 78,
  height: 132,
  start: START,
  rows: [
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~^~^~~*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~****^^^^^****~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~******^^^^^*****~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~**************^*^^~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*************^***~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*^^^***#*****~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*=*~~*~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~,,~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~,,,,,,~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~,,.#.,,~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~,,#,,~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~#~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~.~..~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~^^.......~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~..*******..~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~...*******...~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~.^.............~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~',
    '~~~~,~,~~~~~~~~~~~~~~~~~~~~~~~~~.^..............~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~',
    '~~~,,,,,~~~~~~~~~~~~~~~~~~~~~~~........#.......~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~',
    '~~~,.#.,,~~~~~~~~~~~~~~~~~~~~~~~.......#........~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~',
    '~~,,,#,,~~~~~~~~~~~~~~~~~~~~~~~~.^.....#.....^^~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~',
    '~~~~~#~~~~~~~~~~~~~~~~~~~~~~~~~~~^....^#.......~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~',
    '~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~..^^^#......~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~',
    '~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~....#....~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~',
    '~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~.~#~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~',
    '~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~',
    '~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~',
    '~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~',
    '~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~',
    '~~~~~=~~~~~~~~~^~^~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~',
    '~~~~~=~~~~~~~..^^^^~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~=~~~~~',
    '~~~~~=~~~~~^^..^^.^^..~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~,,~~,,,~~~~~~=~~~~~',
    '~~~~~=~~~~^^^...^..^...~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~,~,,,,,,,,,,~~~~=~~~~~',
    '~~~~~=~~~^^^^..........~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~,,,,,,,,,,~~,~~~=~~~~~',
    '~~~~~=~~~~^^^....^^....~~~~~~~~~~~~~~~.=~.~~~~~~~~~~~~~,,~~,~~,,,,~,,~~~=~~~~~',
    '~~~~~=~~~~......########============####f..~~~~~~~~~~~~~,,,,,,########===~~~~~',
    '~~~~~=~~~.......#.^^..^~~~~~~~~~~fff...#f....~~~~~~~~~~,,,,,,,#,,,,,,~~~~~~~~~',
    '~~~~~=~~~~......#.....^^~~~~~~~~~.f....#..ff..~~~~~~~~~~,,,,,,#,,,,,,~~~~~~~~~',
    '~~~~~=~~~~~.....#.....~~~~~~~~~~.......#......~~~~~~~~~~~,,,,,=~,,,,~~~~~~~~~~',
    '~~~~~=~~~~~~..^.#....~~~~~~~~~~~~......########===========####=,,,~~~~~~~~~~~~',
    '~~~~~=~~~~~~~.^.#..~~~~~~~~~~~~~.......#......~~~~~~~~~~~~~~~,=~,~~~~~~~~~~~~~',
    '~~~~~=~~~~~~~~~~#.~~~~~~~~~~~~~~~......#......~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~',
    '~~~~~=~~~~~~~~~~=~~~~~~~~~~~~~~~~~.....#.....~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~',
    '~~~~~=~~~~~~~~~~=~~~~~~~~~~~~~~~~~~....#...~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~',
    '~~~~~=~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~.=~.~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~',
    '~~~~~=~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~',
    '~~~~~=~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~',
    '~~~~~=~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~',
    '~~~~~=~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~',
    '~~~~~=~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~',
    '~~~~~=~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~.~#~~~~~~~~~~~~~~~',
    '~~~~~=~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~."..#...~~~~~~~~~~~~',
    '~~~~~=~~~~~~~~..=~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~..."..#"....~~~~~~~~~~',
    '~~~~~=~~~~......#..~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~...."..#.....~~~~~~~~~~',
    '~~~~~=~~~.......#...~~~~~~~~~~~~~~~.~.~=.~~~~~~~~~~~~~..""".."#.....~~~~~~~~~~',
    '~~~~~=~~...^^^..#.^^^~~~~~~~~~~~~......#..~~~~~~~~~~~~.......##.....~~~~~~~~~~',
    '~~~~~=~~....^...#.^^^^~~~~~~~~~.....ff.#....~~~~~~~~~~~......#"".....~~~~~~~~~',
    '~~~~~==##########..^^~~~~~~~~~f......f.#.....~~~~~~~~~~~.".."#....".~~~~~~~~~~',
    '~~~~~~~~...^..#.^...^^~~~~~~~ff.f......#.....~~~~~~~~~~~.""."#...."~~~~~~~~~~~',
    '~~~~~~~~.....^######=========#################============####...~~~~~~~~~~~~~',
    '~~~~~~~~.....^#.....~~~~~~~~~~.ff....#....ff.~~~~~~~~~~~~~~~.=.~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~...#....~~~~~~~~~~~~...ff.#.ff....~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~#.~~~~~~~~~~~~~~~~..f.f#......~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~ff..#....~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~.~#~~.~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~,~~~~',
    '~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~,,,,,~~',
    '~~~~~~~~~~~~~~=~~~~~=~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~=~~~~~~~~,..#..~~',
    '~~~~~~~~~~~~~~=~~~~~=~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~.~.~~=~~~~~~~~~,,#,,,~',
    '~~~~~~~~~~~~~~=~~~~~=~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~.......#.~~~~~~~~~,=,~~~',
    '~~~~~~~~~~~~~~#~~~~~=~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~.....f..#..~~~~~~~~~=~~~~',
    '~~~~~~~~~~....#...~~=~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~ff...f..#...~~~~~~~~=~~~~',
    '~~~~~~~~~.....#ff...=~~~~~~~~~~~~~~.~=.~~~~~~~~~~~~~...~~~~~~=....~~~~~~~=~~~~',
    '~~~~~~~~......#.....=~~~~~~~~~~~~....#..~~~~~~~~~~~....~~~====####========~~~~',
    '~~~~~~~~......#...f.#~~~~~~~~~.......#....~~~~~~~~~~......#......~~~~~~~~~~~~~',
    '~~~~~~==##############========########.....~~~~~~~~~......#......~~~~~~~~~~~~~',
    '~~~~~~=......f......#~~~~~~~~.......##.....~~~~~~~~~~.....#......~~~~~~~~~~~~~',
    '~~~~~~=~.f..........#~~~~~~~~~.^....########==========#####....~~~~~~~~~~~~~~~',
    '~~~~~~=~~f..........=~~~~~~~~..^...........~~~~~~~~~~~~~.~.~~~~~~~~~~~~~~~~~~~',
    '~~~~~~=~~~.........~=~~~~~~~~~.............~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~=~~~~~~~.~~~~~=~~~~~~~~~~...........~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~=~~~~~~~~~~~~~=~~~~~~~~~~~........~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~=~~~~~~~~~~~~~=~~~~~~~~~~~~~~.~~.~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~=~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~=~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~=~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~=~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~=~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~=~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~=~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~=~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~,=,~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~,,#,,~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~..#.,,~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~,,,,,,~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~,~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~=~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~---~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~---~~-~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~--------------~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~------------------~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~---------------^^--~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~------------------~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~--------------~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~-~~-~-~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~',
  ],
  /**
   * The gates, in the kingdoms' own order — which is also, walking the roads,
   * roughly the order a student meets them. Each one stands on a road tile, so
   * arriving at a kingdom is the end of a road rather than a scramble across
   * a field.
   */
  landmarks: [
    { kingdomId: 'kingdom-starter-village', x: 14, y: 100 },
    { kingdomId: 'kingdom-number', x: 36, y: 102 },
    { kingdomId: 'kingdom-fraction', x: 58, y: 98 },
    { kingdomId: 'kingdom-algebra', x: 37, y: 80 },
    { kingdomId: 'kingdom-geometry', x: 14, y: 78 },
    { kingdomId: 'kingdom-linear-algebra', x: 61, y: 76 },
    { kingdomId: 'kingdom-function', x: 39, y: 60 },
    { kingdomId: 'kingdom-statistics', x: 62, y: 56 },
    { kingdomId: 'kingdom-trigonometry', x: 16, y: 56 },
    { kingdomId: 'kingdom-calculus', x: 39, y: 39 },
  ],
  /**
   * The isle landings, each at the far end of its causeway.
   *
   * Placed off the coast nearest the work that opens them — First Light on the
   * south-west shore by Starter Village, the Long Watch off the north-west cape
   * where the Geometry road runs, Ledger Bank on the eastern trading coast
   * below Linear Algebra, and the Far Rock in the northern sea past the
   * Calculus cape. Walking onto a landing is always allowed; what a locked isle
   * withholds is what is *on* it.
   */
  isles: [
    { isleId: 'isle-first-light', x: 6, y: 118 },
    { isleId: 'isle-long-watch', x: 4, y: 40 },
    { isleId: 'isle-ledger-bank', x: 73, y: 92 },
    { isleId: 'isle-far-rock', x: 73, y: 30 },
    { isleId: 'isle-north-reach', x: 39, y: 7 },
    { isleId: 'isle-south-reach', x: 20, y: 124 },
  ],
};
